import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HiPlus, HiTrash } from 'react-icons/hi';
import { Modal, Button, Table, TableColumn } from '@/components/common';
import { FormattedNumberInput } from '@/utils/number-format';
import { QUANTITY_DECIMAL_PLACES } from '@/constants/number';
import { useUIStore } from '@/store/uiStore';
import { cuttingApi } from '@/api/endpoints/cutting';
import {
  CreateCutConfigRequest,
  CreateCutConfigLineRequest,
  CutType
} from '@/types/cutting';

const CuttingConfigModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.cuttingConfig;

  const [formData, setFormData] = useState<CreateCutConfigRequest>({
    name: '',
    cutType: 'cut_wood',
    materialType: '',
    description: '',
    cutConfigLines: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Cut type options
  const cutTypeOptions: { value: CutType; label: string }[] = [
    { value: 'cut_wood', label: 'Cắt gỗ' },
    { value: 'cut_mica', label: 'Cắt mica' },
    { value: 'cut_metal', label: 'Cắt kim loại' },
    { value: 'cut_plastic', label: 'Cắt nhựa' },
    { value: 'cut_glass', label: 'Cắt kính' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tên cấu hình là bắt buộc';
    }
    if (!formData.materialType.trim()) {
      newErrors.materialType = 'Loại vật liệu là bắt buộc';
    }
    if (formData.cutConfigLines.length === 0) {
      newErrors.cutConfigLines = 'Phải có ít nhất một dòng cắt';
    }

    // Validate each cut config line
    formData.cutConfigLines.forEach((line, index) => {
      if (!line.finalSize.trim()) {
        newErrors[`line_${index}_finalSize`] = 'Kích thước là bắt buộc';
      }
      if (line.quantity <= 0) {
        newErrors[`line_${index}_quantity`] = 'Số lượng phải lớn hơn 0';
      }
      if (!line.unit.trim()) {
        newErrors[`line_${index}_unit`] = 'Đơn vị là bắt buộc';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await cuttingApi.createCutConfig(formData);
      console.log('Cut config created successfully');
      handleClose();
    } catch (error) {
      console.error('Error creating cut config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('cuttingConfig');
    setFormData({
      name: '',
      cutType: 'cut_wood',
      materialType: '',
      description: '',
      cutConfigLines: [],
    });
    setErrors({});
  };

  const handleInputChange = (field: keyof CreateCutConfigRequest, value: string | CutType) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddCutLine = () => {
    const newLine: CreateCutConfigLineRequest = {
      finalSize: '',
      quantity: 1,
      unit: 'tấm',
      notes: '',
    };

    setFormData(prev => ({
      ...prev,
      cutConfigLines: [...prev.cutConfigLines, newLine],
    }));
  };

  const handleRemoveCutLine = (index: number) => {
    setFormData(prev => ({
      ...prev,
      cutConfigLines: prev.cutConfigLines.filter((_, i) => i !== index),
    }));
  };

  const handleCutLineChange = (
    index: number,
    field: keyof CreateCutConfigLineRequest,
    value: string | number
  ) => {
    setFormData(prev => ({
      ...prev,
      cutConfigLines: prev.cutConfigLines.map((line, i) =>
        i === index ? { ...line, [field]: value } : line
      ),
    }));

    // Clear related errors
    const errorKey = `line_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  // Generate display values for readonly fields
  const configId = 'Tự động tạo';
  const createdBy = 'Người dùng hiện tại';

  // Cut config lines table columns
  const cutLineColumns: TableColumn<CreateCutConfigLineRequest>[] = [
    {
      key: 'finalSize',
      title: 'Kích thước cuối',
      render: (_, record, index) => (
        <input
          type="text"
          value={record.finalSize}
          onChange={(e) => handleCutLineChange(index, 'finalSize', e.target.value)}
          className={`input-field ${errors[`line_${index}_finalSize`] ? 'border-red-500' : ''}`}
          placeholder="VD: 1200x800x18mm"
          disabled={loading}
        />
      ),
    },
    {
      key: 'quantity',
      title: 'Số lượng',
      width: 100,
      render: (_, record, index) => (
        <FormattedNumberInput
          value={record.quantity}
          onChange={(value) => handleCutLineChange(index, 'quantity', Math.max(1, value))}
          className={`input-field ${errors[`line_${index}_quantity`] ? 'border-red-500' : ''}`}
          maxDecimalPlaces={QUANTITY_DECIMAL_PLACES}
          disabled={loading}
        />
      ),
    },
    {
      key: 'unit',
      title: 'Đơn vị',
      width: 100,
      render: (_, record, index) => (
        <input
          type="text"
          value={record.unit}
          onChange={(e) => handleCutLineChange(index, 'unit', e.target.value)}
          className={`input-field ${errors[`line_${index}_unit`] ? 'border-red-500' : ''}`}
          placeholder="VD: tấm"
          disabled={loading}
        />
      ),
    },
    {
      key: 'notes',
      title: 'Ghi chú',
      render: (_, record, index) => (
        <input
          type="text"
          value={record.notes || ''}
          onChange={(e) => handleCutLineChange(index, 'notes', e.target.value)}
          className="input-field"
          placeholder="Ghi chú"
          disabled={loading}
        />
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 50,
      align: 'center',
      render: (_, __, index) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleRemoveCutLine(index)}
          icon={<HiTrash className="h-4 w-4" />}
          className="text-red-600 hover:text-red-700 hover:border-red-300"
          title={t('tooltips.cutting.deleteLine')}
          disabled={loading}
        />
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('tooltips.cutting.cutConfig')} // Title positioned top-left as specified
      size="xl"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <div className="space-y-6">
        {/* Top Form Section - two-column layout as specified */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Config ID (readonly) */}
          <div>
            <label htmlFor="configId" className="block text-sm font-medium text-gray-700 mb-1">
              Config ID
            </label>
            <input
              type="text"
              id="configId"
              value={configId}
              readOnly
              className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Created By (readonly) */}
          <div>
            <label htmlFor="createdBy" className="block text-sm font-medium text-gray-700 mb-1">
              Người tạo
            </label>
            <input
              type="text"
              id="createdBy"
              value={createdBy}
              readOnly
              className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Cut Type */}
          <div>
            <label htmlFor="cutType" className="block text-sm font-medium text-gray-700 mb-1">
              Loại cắt *
            </label>
            <select
              id="cutType"
              value={formData.cutType}
              onChange={(e) => handleInputChange('cutType', e.target.value as CutType)}
              className="input-field"
              disabled={loading}
            >
              {cutTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Material Type */}
          <div>
            <label htmlFor="materialType" className="block text-sm font-medium text-gray-700 mb-1">
              Loại vật liệu *
            </label>
            <input
              type="text"
              id="materialType"
              value={formData.materialType}
              onChange={(e) => handleInputChange('materialType', e.target.value)}
              className={`input-field ${errors.materialType ? 'border-red-500' : ''}`}
              placeholder="VD: MDF, Mica, Thép..."
              disabled={loading}
            />
            {errors.materialType && <p className="text-red-500 text-xs mt-1">{errors.materialType}</p>}
          </div>
        </div>

        {/* Config Name - full width */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Tên cấu hình *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`input-field ${errors.name ? 'border-red-500' : ''}`}
            placeholder="VD: MDF 1200x800x18mm - Tủ bếp"
            disabled={loading}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Mô tả
          </label>
          <textarea
            id="description"
            rows={2}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="input-field resize-none"
            placeholder="Mô tả cấu hình cắt..."
            disabled={loading}
          />
        </div>

        {/* Cut List Table Section - appears below form as specified */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Danh sách cắt</h3>
            
            {/* Add Cut Line Button - positioned above table, right-aligned as specified */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCutLine}
              icon={<HiPlus className="h-4 w-4" />}
              disabled={loading}
            >
              + Thêm mới
            </Button>
          </div>

          {/* Cut List Table showing Final Size, Quantity, Unit, Note as specified */}
          <div className="border border-gray-200 rounded-lg">
            <Table
              columns={cutLineColumns}
              data={formData.cutConfigLines}
              rowKey={(record) => `cutline-${record.finalSize}-${record.quantity}`}
              size="sm"
              emptyText="Chưa có dòng cắt nào. Nhấn '+ Thêm mới' để bắt đầu."
            />
          </div>

          {errors.cutConfigLines && <p className="text-red-500 text-xs">{errors.cutConfigLines}</p>}
        </div>

        {/* Save Button - positioned bottom-right corner as specified */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Hủy
            </Button>
            
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
            >
              Lưu cấu hình
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CuttingConfigModal;
